import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useNavigate,
  useOutletContext,
} from 'react-router-dom';

import {
  BarChart3,
  Bell,
  Building2,
  CreditCard,
  FileText,
  IndianRupee,
  LayoutDashboard,
  LogOut,
  Plus,
  Receipt,
  Search,
  Settings,
  Sparkles,
  Wallet,
} from 'lucide-react';

import './ControlCenterPayments.css';

const API_BASE_URL = window.location.origin;

const getToken = () =>
  localStorage.getItem('vakra_control_admin_token') ||
  sessionStorage.getItem('vakra_control_admin_token');

const ControlCenterPayments = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};

  let savedAdmin = null;

  try {
    const rawAdmin =
      localStorage.getItem('vakra_control_admin') ||
      sessionStorage.getItem('vakra_control_admin');

    if (rawAdmin) {
      savedAdmin = JSON.parse(rawAdmin);
    }
  } catch (error) {
    console.error('Saved admin parse error:', error);
  }

  const admin =
    outletContext?.admin ||
    savedAdmin ||
    null;

  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const getToday = () =>
    new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    subscriptionId: '',
    amount: '',
    paymentMode: 'upi',
    transactionId: '',
    paymentDate: getToday(),
    status: 'paid',
    notes: '',
  });

  const resetForm = () => {
    setForm({
      subscriptionId: '',
      amount: '',
      paymentMode: 'upi',
      transactionId: '',
      paymentDate: getToday(),
      status: 'paid',
      notes: '',
    });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const token = getToken();

      const [
        paymentResponse,
        subscriptionResponse,
      ] = await Promise.all([
        fetch(
          `${API_BASE_URL}/api/control-center/payments`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),

        fetch(
          `${API_BASE_URL}/api/control-center/subscriptions`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);

      const paymentData =
        await paymentResponse
          .json()
          .catch(() => ({}));

      const subscriptionData =
        await subscriptionResponse
          .json()
          .catch(() => ({}));

      if (!paymentResponse.ok) {
        throw new Error(
          paymentData.error ||
            'Payments load nahi ho paye.'
        );
      }

      if (!subscriptionResponse.ok) {
        throw new Error(
          subscriptionData.error ||
            'Subscriptions load nahi ho paye.'
        );
      }

      setPayments(
        Array.isArray(paymentData.payments)
          ? paymentData.payments
          : []
      );

      setSubscriptions(
        Array.isArray(
          subscriptionData.subscriptions
        )
          ? subscriptionData.subscriptions
          : []
      );
    } catch (loadError) {
      console.error(
        'Payment load error:',
        loadError
      );

      setError(
        loadError.message ||
          'Payment data load nahi ho paya.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const selectedSubscription =
    subscriptions.find(
      (item) =>
        String(item.subscriptionId) ===
        String(form.subscriptionId)
    );

  const stats = useMemo(() => {
    const paidPayments =
      payments.filter(
        (item) =>
          item.status === 'paid'
      );

    const today = getToday();
    const currentMonth =
      today.slice(0, 7);

    const todayCollection =
      paidPayments
        .filter(
          (item) =>
            item.paymentDate === today
        )
        .reduce(
          (total, item) =>
            total +
            Number(item.amount || 0),
          0
        );

    const monthlyCollection =
      paidPayments
        .filter(
          (item) =>
            String(
              item.paymentDate || ''
            ).startsWith(
              currentMonth
            )
        )
        .reduce(
          (total, item) =>
            total +
            Number(item.amount || 0),
          0
        );

    const totalCollection =
      paidPayments.reduce(
        (total, item) =>
          total +
          Number(item.amount || 0),
        0
      );

    const pendingAmount =
      payments
        .filter(
          (item) =>
            item.status === 'pending'
        )
        .reduce(
          (total, item) =>
            total +
            Number(item.amount || 0),
          0
        );

    return {
      todayCollection,
      monthlyCollection,
      totalCollection,
      pendingAmount,
      totalPayments: payments.length,
    };
  }, [payments]);

  const filteredPayments =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      return payments.filter(
        (payment) => {
          const matchesSearch =
            !query ||
            String(
              payment.businessName || ''
            )
              .toLowerCase()
              .includes(query) ||
            String(
              payment.ownerName || ''
            )
              .toLowerCase()
              .includes(query) ||
            String(
              payment.mobile || ''
            )
              .toLowerCase()
              .includes(query) ||
            String(
              payment.planName || ''
            )
              .toLowerCase()
              .includes(query) ||
            String(
              payment.receiptNo || ''
            )
              .toLowerCase()
              .includes(query) ||
            String(
              payment.transactionId || ''
            )
              .toLowerCase()
              .includes(query);

          const matchesStatus =
            statusFilter === 'all' ||
            payment.status ===
              statusFilter;

          return (
            matchesSearch &&
            matchesStatus
          );
        }
      );
    }, [
      payments,
      search,
      statusFilter,
    ]);

  const handleSubscriptionChange =
    (event) => {
      const subscriptionId =
        event.target.value;

      const subscription =
        subscriptions.find(
          (item) =>
            String(
              item.subscriptionId
            ) ===
            String(
              subscriptionId
            )
        );

      setForm(
        (previous) => ({
          ...previous,
          subscriptionId,
          amount:
            subscription
              ? String(
                  subscription.amount
                )
              : '',
        })
      );

      setError('');
    };

  const handleSavePayment =
    async (event) => {
      event.preventDefault();

      setError('');
      setSuccessMessage('');

      if (!form.subscriptionId) {
        setError(
          'Vendor subscription select karein.'
        );
        return;
      }

      if (
        !form.amount ||
        Number(form.amount) <= 0
      ) {
        setError(
          'Valid payment amount enter karein.'
        );
        return;
      }

      if (!form.paymentMode) {
        setError(
          'Payment mode select karein.'
        );
        return;
      }

      if (!form.paymentDate) {
        setError(
          'Payment date select karein.'
        );
        return;
      }

      if (!selectedSubscription) {
        setError(
          'Selected subscription nahi mila.'
        );
        return;
      }

      if (
        form.status === 'paid' &&
        form.paymentMode !== 'cash' &&
        !form.transactionId.trim()
      ) {
        setError(
          'UPI, Card ya Bank payment ke liye transaction/reference ID enter karein.'
        );
        return;
      }

      try {
        setSaving(true);

        const response =
          await fetch(
            `${API_BASE_URL}/api/control-center/payments`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${getToken()}`,
              },

              body:
                JSON.stringify({
                  subscriptionId:
                    selectedSubscription
                      .subscriptionId,

                  sourceDatabase:
                    selectedSubscription
                      .sourceDatabase,

                  vendorId:
                    selectedSubscription
                      .originalVendorId,

                  amount:
                    Number(
                      form.amount
                    ),

                  paymentMode:
                    form.paymentMode,

                  transactionId:
                    form.transactionId
                      .trim(),

                  paymentDate:
                    form.paymentDate,

                  status:
                    form.status,

                  notes:
                    form.notes
                      .trim(),
                }),
            }
          );

        const data =
          await response
            .json()
            .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data.error ||
              'Payment save nahi hua.'
          );
        }

        setShowModal(false);

        resetForm();

        setSuccessMessage(
          data.payment?.receiptNo
            ? `Payment saved. Receipt: ${data.payment.receiptNo}`
            : 'Payment successfully saved.'
        );

        await loadData();
      } catch (saveError) {
        setError(
          saveError.message ||
            'Payment save nahi ho paya.'
        );
      } finally {
        setSaving(false);
      }
    };

  const handleLogout = async () => {
    const token = getToken();

    try {
      if (token) {
        await fetch(
          `${API_BASE_URL}/api/control-center/logout`,
          {
            method: 'POST',
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );
      }
    } catch (logoutError) {
      console.error(
        'Logout error:',
        logoutError
      );
    }

    localStorage.removeItem(
      'vakra_control_admin_token'
    );

    localStorage.removeItem(
      'vakra_control_admin'
    );

    sessionStorage.removeItem(
      'vakra_control_admin_token'
    );

    sessionStorage.removeItem(
      'vakra_control_admin'
    );

    navigate(
      '/control-center/login',
      {
        replace: true,
      }
    );
  };

  const menuItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path:
        '/control-center/dashboard',
    },
    {
      label: 'Vendors',
      icon: Building2,
      path:
        '/control-center/vendors',
    },
    {
      label: 'Subscriptions',
      icon: CreditCard,
      path:
        '/control-center/subscriptions',
    },
    {
      label: 'Payments',
      icon: Wallet,
      path:
        '/control-center/payments',
      active: true,
    },
    {
      label: 'Agreements',
      icon: FileText,
      path:
        '/control-center/agreements',
    },
    {
      label: 'Smart Offer Digest',
      icon: Sparkles,
      path:
        '/control-center/smart-digest',
    },
    {
      label: 'Analytics',
      icon: BarChart3,
      path:
        '/control-center/analytics',
    },
    {
      label: 'Settings',
      icon: Settings,
      path:
        '/control-center/settings',
    },
  ];

  const handleMenuClick =
    (item) => {
      if (
        item.path ===
          '/control-center/dashboard' ||
        item.path ===
          '/control-center/vendors' ||
        item.path ===
          '/control-center/subscriptions' ||
        item.path ===
          '/control-center/payments'
      ) {
        navigate(item.path);
        return;
      }

      alert(
        `${item.label} page next phase me banega.`
      );
    };

  const formatMode = (mode) => {
    if (
      mode === 'bank_transfer'
    ) {
      return 'Bank Transfer';
    }

    if (mode === 'upi') {
      return 'UPI';
    }

    if (mode === 'cash') {
      return 'Cash';
    }

    if (mode === 'card') {
      return 'Card';
    }

    return 'Other';
  };

  return (
    <div className="vccp-app">
      <aside className="vccp-sidebar">
        <div className="vccp-brand">
          <div className="vccp-logo">
            V
          </div>

          <div>
            <strong>
              VAKRA
            </strong>

            <span>
              CONTROL CENTER
            </span>
          </div>
        </div>

        <div className="vccp-admin">
          <div className="vccp-avatar">
            {String(
              admin?.name || 'A'
            )
              .charAt(0)
              .toUpperCase()}
          </div>

          <div>
            <strong>
              {admin?.name ||
                'Administrator'}
            </strong>

            <span>
              {admin?.role ||
                'admin'}
            </span>
          </div>
        </div>

        <nav>
          {menuItems.map(
            (item) => {
              const Icon =
                item.icon;

              return (
                <button
                  type="button"
                  key={
                    item.label
                  }
                  className={
                    item.active
                      ? 'active'
                      : ''
                  }
                  onClick={() =>
                    handleMenuClick(
                      item
                    )
                  }
                >
                  <Icon
                    size={18}
                  />

                  {
                    item.label
                  }
                </button>
              );
            }
          )}
        </nav>

        <button
          type="button"
          className="vccp-logout"
          onClick={
            handleLogout
          }
        >
          <LogOut
            size={18}
          />

          Logout
        </button>
      </aside>

      <main className="vccp-main">
        <header className="vccp-topbar">
          <div>
            <span>
              VAKRA CONTROL CENTER
            </span>

            <h1>
              Payments
            </h1>
          </div>

          <button
            type="button"
            className="vccp-bell"
          >
            <Bell
              size={20}
            />
          </button>
        </header>

        <div className="vccp-content">
          <section className="vccp-heading">
            <div>
              <span>
                <Wallet
                  size={16}
                />
                Payment Management
              </span>

              <h2>
                Vendor Payments
              </h2>

              <p>
                Subscription payment,
                transaction reference,
                payment status aur
                receipt history ek
                jagah se manage karein.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setError('');
                setSuccessMessage('');
                setShowModal(true);
              }}
            >
              <Plus
                size={18}
              />

              Record Payment
            </button>
          </section>

          {successMessage && (
            <div className="vccp-success">
              {successMessage}
            </div>
          )}

          <section className="vccp-stats">
            <article>
              <div className="vccp-stat-icon">
                <IndianRupee
                  size={22}
                />
              </div>

              <span>
                Today Collection
              </span>

              <strong>
                ₹
                {stats.todayCollection.toLocaleString(
                  'en-IN'
                )}
              </strong>

              <small>
                Paid today
              </small>
            </article>

            <article>
              <div className="vccp-stat-icon">
                <Wallet
                  size={22}
                />
              </div>

              <span>
                This Month
              </span>

              <strong>
                ₹
                {stats.monthlyCollection.toLocaleString(
                  'en-IN'
                )}
              </strong>

              <small>
                Monthly paid amount
              </small>
            </article>

            <article>
              <div className="vccp-stat-icon">
                <Receipt
                  size={22}
                />
              </div>

              <span>
                Total Collection
              </span>

              <strong>
                ₹
                {stats.totalCollection.toLocaleString(
                  'en-IN'
                )}
              </strong>

              <small>
                All paid payments
              </small>
            </article>

            <article>
              <div className="vccp-stat-icon warning">
                <CreditCard
                  size={22}
                />
              </div>

              <span>
                Pending
              </span>

              <strong>
                ₹
                {stats.pendingAmount.toLocaleString(
                  'en-IN'
                )}
              </strong>

              <small>
                Pending payment amount
              </small>
            </article>
          </section>

          <section className="vccp-panel">
            <div className="vccp-toolbar">
              <div className="vccp-search">
                <Search
                  size={18}
                />

                <input
                  value={search}
                  onChange={(event) =>
                    setSearch(
                      event.target.value
                    )
                  }
                  placeholder="Search vendor, mobile, receipt or transaction ID"
                />
              </div>

              <select
                value={
                  statusFilter
                }
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
              >
                <option value="all">
                  All Status
                </option>

                <option value="paid">
                  Paid
                </option>

                <option value="pending">
                  Pending
                </option>

                <option value="refunded">
                  Refunded
                </option>

                <option value="cancelled">
                  Cancelled
                </option>
              </select>
            </div>

            <div className="vccp-table-heading">
              <div>
                <h3>
                  Payment History
                </h3>

                <p>
                  {
                    filteredPayments.length
                  } payment record
                  {filteredPayments.length ===
                  1
                    ? ''
                    : 's'}
                </p>
              </div>
            </div>

            {error &&
              !showModal && (
                <div className="vccp-error">
                  {error}
                </div>
              )}

            {loading ? (
              <div className="vccp-empty">
                <div className="vccp-spinner" />

                <strong>
                  Loading payments...
                </strong>
              </div>
            ) : filteredPayments.length ===
              0 ? (
              <div className="vccp-empty">
                <div className="vccp-empty-icon">
                  <Receipt
                    size={30}
                  />
                </div>

                <strong>
                  No payment records
                </strong>

                <span>
                  Record Payment button
                  se vendor ka payment
                  add karein.
                </span>
              </div>
            ) : (
              <div className="vccp-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Mode</th>
                      <th>Transaction</th>
                      <th>Date</th>
                      <th>Receipt</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredPayments.map(
                      (payment) => (
                        <tr
                          key={
                            payment.paymentId
                          }
                        >
                          <td>
                            <strong>
                              {
                                payment.businessName
                              }
                            </strong>

                            <small>
                              {
                                payment.mobile ||
                                '—'
                              }
                            </small>
                          </td>

                          <td>
                            {
                              payment.planName ||
                              'No Plan'
                            }
                          </td>

                          <td className="vccp-amount">
                            ₹
                            {Number(
                              payment.amount ||
                                0
                            ).toLocaleString(
                              'en-IN'
                            )}
                          </td>

                          <td>
                            {formatMode(
                              payment.paymentMode
                            )}
                          </td>

                          <td>
                            {
                              payment.transactionId ||
                              '—'
                            }
                          </td>

                          <td>
                            {
                              payment.paymentDate ||
                              '—'
                            }
                          </td>

                          <td>
                            {payment.receiptNo ? (
                              <span className="vccp-receipt-number">
                                {
                                  payment.receiptNo
                                }
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>

                          <td>
                            <span
                              className={`vccp-status ${
                                payment.status ||
                                'pending'
                              }`}
                            >
                              {
                                payment.status ||
                                'pending'
                              }
                            </span>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </main>

      {showModal && (
        <div className="vccp-modal-backdrop">
          <form
            className="vccp-modal"
            onSubmit={
              handleSavePayment
            }
          >
            <div className="vccp-modal-heading">
              <div>
                <span>
                  PAYMENT ENTRY
                </span>

                <h3>
                  Record Payment
                </h3>

                <p>
                  Vendor ki subscription
                  select karke received
                  payment save karein.
                </p>
              </div>
            </div>

            {error && (
              <div className="vccp-error vccp-modal-error">
                {error}
              </div>
            )}

            <label>
              Vendor Subscription

              <select
                value={
                  form.subscriptionId
                }
                onChange={
                  handleSubscriptionChange
                }
              >
                <option value="">
                  Select Vendor Subscription
                </option>

                {subscriptions.map(
                  (item) => (
                    <option
                      key={
                        item.subscriptionId
                      }
                      value={
                        item.subscriptionId
                      }
                    >
                      {
                        item.businessName
                      }
                      {' | '}
                      {
                        item.planName
                      }
                      {' | ₹'}
                      {
                        item.amount
                      }
                    </option>
                  )
                )}
              </select>
            </label>

            {selectedSubscription && (
              <div className="vccp-subscription-info">
                <strong>
                  {
                    selectedSubscription.businessName
                  }
                </strong>

                <span>
                  {
                    selectedSubscription.ownerName ||
                    ''
                  }
                </span>

                <div>
                  <span>
                    Plan:{' '}
                    <b>
                      {
                        selectedSubscription.planName
                      }
                    </b>
                  </span>

                  <span>
                    Amount:{' '}
                    <b>
                      ₹
                      {
                        selectedSubscription.amount
                      }
                    </b>
                  </span>
                </div>

                <div>
                  <span>
                    Start:{' '}
                    <b>
                      {
                        selectedSubscription.startDate
                      }
                    </b>
                  </span>

                  <span>
                    Expiry:{' '}
                    <b>
                      {
                        selectedSubscription.expiryDate
                      }
                    </b>
                  </span>
                </div>
              </div>
            )}

            <div className="vccp-form-grid">
              <label>
                Amount

                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={
                    form.amount
                  }
                  onChange={(event) =>
                    setForm(
                      (previous) => ({
                        ...previous,
                        amount:
                          event.target
                            .value,
                      })
                    )
                  }
                  placeholder="Payment amount"
                />
              </label>

              <label>
                Payment Mode

                <select
                  value={
                    form.paymentMode
                  }
                  onChange={(event) =>
                    setForm(
                      (previous) => ({
                        ...previous,
                        paymentMode:
                          event.target
                            .value,
                      })
                    )
                  }
                >
                  <option value="upi">
                    UPI
                  </option>

                  <option value="cash">
                    Cash
                  </option>

                  <option value="bank_transfer">
                    Bank Transfer
                  </option>

                  <option value="card">
                    Card
                  </option>

                  <option value="other">
                    Other
                  </option>
                </select>
              </label>

              <label>
                Payment Date

                <input
                  type="date"
                  value={
                    form.paymentDate
                  }
                  onChange={(event) =>
                    setForm(
                      (previous) => ({
                        ...previous,
                        paymentDate:
                          event.target
                            .value,
                      })
                    )
                  }
                />
              </label>

              <label>
                Payment Status

                <select
                  value={
                    form.status
                  }
                  onChange={(event) =>
                    setForm(
                      (previous) => ({
                        ...previous,
                        status:
                          event.target
                            .value,
                      })
                    )
                  }
                >
                  <option value="paid">
                    Paid
                  </option>

                  <option value="pending">
                    Pending
                  </option>
                </select>
              </label>
            </div>

            <label>
              Transaction / Reference ID

              <input
                value={
                  form.transactionId
                }
                onChange={(event) =>
                  setForm(
                    (previous) => ({
                      ...previous,
                      transactionId:
                        event.target
                          .value,
                    })
                  )
                }
                placeholder={
                  form.paymentMode ===
                  'cash'
                    ? 'Cash payment ke liye optional'
                    : 'Transaction / UTR / Reference ID'
                }
              />
            </label>

            <label>
              Notes

              <textarea
                value={
                  form.notes
                }
                onChange={(event) =>
                  setForm(
                    (previous) => ({
                      ...previous,
                      notes:
                        event.target
                          .value,
                    })
                  )
                }
                placeholder="Optional payment notes"
              />
            </label>

            <div className="vccp-modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setError('');
                  resetForm();
                }}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
              >
                {saving
                  ? 'Saving Payment...'
                  : 'Save Payment'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ControlCenterPayments;
